import {
  DEFAULT_TYPESENSE_COLLECTION,
  TYPESENSE_SEARCH_STATUS_PUBLISH,
} from '../constants';
import { typesenseServerClient } from './server-client';

export type ProductVariantSibling = {
  product_id: number;
  label: string;
  slug: string;
  sku: string;
  name: string;
  in_stock: boolean;
  sale_price?: number;
};

export type ProductVariantGroupResult = {
  id: number;
  variant_type: string;
  variant_type_label?: string;
  items: ProductVariantSibling[];
};

const VARIANT_INCLUDE_FIELDS =
  'product_id,slug,sku,name,variant_label,variant_type,variant_type_label,in_stock,sale_price';

export async function getProductVariantsByGroupId(
  variantGroupId: number,
): Promise<ProductVariantGroupResult | null> {
  if (!variantGroupId) {
    return null;
  }

  try {
    const result = await typesenseServerClient
      .collections(DEFAULT_TYPESENSE_COLLECTION)
      .documents()
      .search({
        q: '*',
        query_by: 'name',
        filter_by: `${TYPESENSE_SEARCH_STATUS_PUBLISH} && variant_group_id:=${variantGroupId}`,
        include_fields: VARIANT_INCLUDE_FIELDS,
        per_page: 30,
      });

    // Sort client-side: variant_label is store-only in Typesense (index/sort false).
    const items =
      result.hits
        ?.map((hit: { document: Record<string, unknown> }) => {
          const doc = hit.document;
          return {
            product_id: Number(doc.product_id),
            label: String(doc.variant_label ?? ''),
            slug: String(doc.slug ?? ''),
            sku: String(doc.sku ?? doc.id ?? ''),
            name: String(doc.name ?? ''),
            in_stock: Boolean(doc.in_stock),
            sale_price:
              doc.sale_price != null ? Number(doc.sale_price) : undefined,
          };
        })
        .toSorted((a, b) =>
          a.label.localeCompare(b.label, 'pt-BR', { numeric: true }),
        ) ?? [];

    if (items.length < 2) {
      return null;
    }

    const variantType =
      (result.hits?.[0]?.document as Record<string, unknown>)?.variant_type ??
      'dosage';

    return {
      id: variantGroupId,
      variant_type: String(variantType),
      variant_type_label:
        ((result.hits?.[0]?.document as Record<string, unknown>)
          ?.variant_type_label as string | undefined) || undefined,
      items,
    };
  } catch (error) {
    console.error('Error fetching product variants from Typesense:', error);
    return null;
  }
}
